import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { listPractices, setFreeAccount, extendTrial } from '../services/platformAdminService';
import Spinner from '../components/common/Spinner';
import authStore from '../store/authStore';

const STATUS_BADGE = {
  trialing:  'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  active:    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  past_due:  'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  canceled:  'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-400',
  unpaid:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const STATUS_LABEL = {
  trialing: 'Trialing',
  active:   'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
  unpaid:   'Unpaid',
};

function fmtDate(d) {
  if (!d) return '—';
  try { return format(parseISO(d), 'MMM d, yyyy'); } catch { return '—'; }
}

function ExtendTrialInput({ practiceId, onSuccess }) {
  const [days, setDays] = useState('30');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => extendTrial(practiceId, parseInt(days, 10)),
    onSuccess: () => {
      toast.success('Trial extended');
      queryClient.invalidateQueries({ queryKey: ['platformAdmin', 'practices'] });
      onSuccess();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to extend trial'),
  });

  return (
    <div className="flex items-center gap-2 mt-2">
      <input
        type="number"
        min={1}
        max={3650}
        value={days}
        onChange={(e) => setDays(e.target.value)}
        className="w-20 px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
      />
      <span className="text-xs text-neutral-500">days</span>
      <button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="text-xs px-2.5 py-1 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
      >
        {mutation.isPending ? 'Saving…' : 'Extend'}
      </button>
    </div>
  );
}

function PracticeRow({ practice }) {
  const queryClient = useQueryClient();
  const [showExtend, setShowExtend] = useState(false);

  const freeAccountMutation = useMutation({
    mutationFn: (enabled) => setFreeAccount(practice.id, enabled),
    onSuccess: (_, enabled) => {
      toast.success(enabled ? 'Complimentary access granted' : 'Complimentary access revoked');
      queryClient.invalidateQueries({ queryKey: ['platformAdmin', 'practices'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const owner = practice.practitioners?.[0]?.user;
  const ownerName = owner ? `${owner.firstName} ${owner.lastName}` : '—';
  const ownerEmail = owner?.email ?? practice.email ?? '—';

  return (
    <tr className="border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      {/* Practice */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center shrink-0">
            <BuildingOfficeIcon className="w-4 h-4 text-teal-600 dark:text-teal-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{practice.name}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">{ownerName} · {ownerEmail}</p>
          </div>
        </div>
      </td>

      {/* Status */}
      <td className="py-3 px-4">
        {practice.freeAccount ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
            <CheckCircleIcon className="w-3 h-3" />
            Complimentary
          </span>
        ) : (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[practice.subscriptionStatus] ?? STATUS_BADGE.canceled}`}>
            {STATUS_LABEL[practice.subscriptionStatus] ?? practice.subscriptionStatus}
          </span>
        )}
      </td>

      {/* Trial / period end */}
      <td className="py-3 px-4 text-sm text-neutral-600 dark:text-neutral-400">
        {practice.freeAccount ? (
          <span className="text-neutral-400 dark:text-neutral-500">—</span>
        ) : practice.subscriptionStatus === 'trialing' ? (
          <span className="flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5" />
            {fmtDate(practice.trialEndsAt)}
          </span>
        ) : practice.subscriptionCurrentPeriodEnd ? (
          fmtDate(practice.subscriptionCurrentPeriodEnd)
        ) : '—'}
      </td>

      {/* Clients */}
      <td className="py-3 px-4 text-sm text-neutral-600 dark:text-neutral-400 text-center">
        {practice.clientCount}
      </td>

      {/* Joined */}
      <td className="py-3 px-4 text-sm text-neutral-500 dark:text-neutral-400">
        {fmtDate(practice.createdAt)}
      </td>

      {/* Actions */}
      <td className="py-3 px-4">
        <div className="flex flex-col gap-1 items-start">
          {/* Free account toggle */}
          <button
            onClick={() => freeAccountMutation.mutate(!practice.freeAccount)}
            disabled={freeAccountMutation.isPending}
            className={`text-xs px-2.5 py-1 rounded-lg border transition-colors disabled:opacity-50 ${
              practice.freeAccount
                ? 'border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                : 'border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
            }`}
          >
            {practice.freeAccount ? 'Revoke complimentary' : 'Grant complimentary'}
          </button>

          {/* Extend trial */}
          {!practice.freeAccount && (
            showExtend ? (
              <ExtendTrialInput
                practiceId={practice.id}
                onSuccess={() => setShowExtend(false)}
              />
            ) : (
              <button
                onClick={() => setShowExtend(true)}
                className="text-xs px-2.5 py-1 rounded-lg border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                Extend trial
              </button>
            )
          )}
        </div>
      </td>
    </tr>
  );
}

export default function PlatformAdmin() {
  const [search, setSearch] = useState('');
  const { logout, user } = authStore();

  const { data: practices = [], isLoading, isError } = useQuery({
    queryKey: ['platformAdmin', 'practices'],
    queryFn: listPractices,
    refetchInterval: 60_000,
  });

  const filtered = practices.filter((p) => {
    const q = search.toLowerCase();
    if (!q) return true;
    const owner = p.practitioners?.[0]?.user;
    return (
      p.name?.toLowerCase().includes(q) ||
      owner?.email?.toLowerCase().includes(q) ||
      owner?.firstName?.toLowerCase().includes(q) ||
      owner?.lastName?.toLowerCase().includes(q)
    );
  });

  const stats = {
    total: practices.length,
    trialing: practices.filter((p) => p.subscriptionStatus === 'trialing' && !p.freeAccount).length,
    active: practices.filter((p) => p.subscriptionStatus === 'active').length,
    complimentary: practices.filter((p) => p.freeAccount).length,
    pastDue: practices.filter((p) => p.subscriptionStatus === 'past_due').length,
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Platform Admin</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Manage practices, trials, and complimentary access</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-500 dark:text-neutral-400">{user?.email}</span>
            <button
              onClick={logout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-neutral-600 dark:text-neutral-400 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Total practices', value: stats.total, color: 'text-neutral-900 dark:text-neutral-100' },
            { label: 'Trialing', value: stats.trialing, color: 'text-blue-600 dark:text-blue-400' },
            { label: 'Active', value: stats.active, color: 'text-green-600 dark:text-green-400' },
            { label: 'Complimentary', value: stats.complimentary, color: 'text-teal-600 dark:text-teal-400' },
            { label: 'Past due', value: stats.pastDue, color: 'text-yellow-600 dark:text-yellow-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-700 px-4 py-3">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{label}</p>
              <p className={`text-2xl font-bold mt-0.5 ${color}`}>{value}</p>
            </div>
          ))}
        </div>

        {/* Search + table */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-800">
            <input
              type="search"
              placeholder="Search practices or owners…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-80 px-3 py-1.5 text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-20"><Spinner /></div>
          ) : isError ? (
            <div className="flex flex-col items-center py-16 text-neutral-500">
              <ExclamationCircleIcon className="w-8 h-8 mb-2 text-red-400" />
              <p>Failed to load practices</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-neutral-400">
              <BuildingOfficeIcon className="w-8 h-8 mb-2" />
              <p>{search ? 'No practices match your search' : 'No practices yet'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide border-b border-neutral-100 dark:border-neutral-800">
                    <th className="py-2.5 px-4">Practice / Owner</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4">Trial / Renewal</th>
                    <th className="py-2.5 px-4 text-center">Clients</th>
                    <th className="py-2.5 px-4">Joined</th>
                    <th className="py-2.5 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => <PracticeRow key={p.id} practice={p} />)}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
